import * as React from "react"
import 'intersection-observer';
import { InView } from 'react-intersection-observer';
import { Updater, IOWidget, fromJSX, Widget, AsyncState } from "widgets-for-react"
import { CMSState, Id, ImageSize, imageGalleryBlockData } from "../types.gen";
import { getClassName } from "../utils.gen";
import { List, Map } from "immutable";
import { ModernImageContainer } from "./ImageContainer.gen";
import { blockInviewHandler } from "../../../drupal/rendering/inview_utils.mod";


export type ImageGalleryBlockProps = {
  index: number
  title?: string | React.ReactNode
  intro?: string
  images: ListWithAtLeast2Items<ImageGalleryImageProps>,
  imagePreviewCount: number,
  get_default_data_src:(() => ImageSize),
  get_default_data_srcset:(() => [ImageSize, string][])
  lightbox_src: (() => ImageSize)
  lightbox_srcset: (() => [ImageSize, string][])
  thumbnail_rectangle_src: (() => ImageSize)
  thumbnail_rectangle_srcset: (() => [ImageSize, string][])
  thumbnail_square_src: (() => ImageSize)
  thumbnail_square_srcset: (() => [ImageSize, string][])
  copy: ImageGalleryCopy
  inview?: {
    threshold?: number
    triggerOnce?: boolean
  }
}

export type ImageGalleryImageProps = {
  index: number
  image: Id
  image_size: ImageSize
  imageDataSizes?: string
  image_alt: string
  is_foreground:boolean
  caption?: string
  source?: string
}

export type ImageGalleryCopy = {
  close: string,
  more: string
  enlarged: string
  previous: string
  next: string
}

export type ImageGalleryBlockStyle = Partial<{
  block_classname: string
  alt_block_classname: string
  inview_classname: string
  inner_classname: string
  content_classname: string
  header_classname: string
  title_classname: string
  intro_classname: string
  gallery_list_classname: string
  alt_gallery_list_classname: string
}> & ImageGalleryItemStyle

const ondoneImage = (
  onDone: (_: Updater<CMSState>) => void,
  props: ImageGalleryBlockProps,
  index: number
) => (action: Updater<"next" | "prev" | "close" | "open">) => {
  let openNew = action("open")
  onDone(s1 => {
    let currentBlock = s1.blocks.get(props.index)
    if (currentBlock.kind === "imageGalleryBlock") {
      currentBlock = {
        ...currentBlock,
        value: [
          {
            ...currentBlock.value[0],
            images: new ListWithAtLeast2Items<ImageGalleryImageProps>(
              currentBlock.value[0].images.items.map((b, b_i) => {
                if (openNew == "next" && b_i - 1 == index) {
                  return { ...b, is_foreground: true }
                }
                if (openNew == "prev" && b_i + 1 == index) {
                  return { ...b, is_foreground: true }
                }
                if (openNew == "open" && b_i == index) {
                  return { ...b, is_foreground: true }
                }
                return { ...b, is_foreground: false }
              }).toList()
            )
          },
          currentBlock.value[1]
        ],
      }
    }
    return { ...s1, blocks: s1.blocks.set(props.index, currentBlock) }
  })
}


export function ImageGalleryBlock(
  props: ImageGalleryBlockProps,
  options?: { style?: ImageGalleryBlockStyle; key?: string }
): IOWidget<CMSState, Updater<CMSState>> {
  return (s: CMSState) =>
    fromJSX(setState => {
        let images = props.images.GetFirst(props.imagePreviewCount)
        let openImage = props.images.items.find(i => i.is_foreground)
        let openImageIndex = props.images.items.findIndex(i => i.is_foreground)
        let isAlternateGalleryBlock = props.images.items.count() == 2

        return (
          <InView
            as="section"
            onChange={(inView) => blockInviewHandler('imageGallery', inView, `io-${options && options.key ? options.key : props.index}`, getClassName(options, "inview_classname", "inview"))}
            triggerOnce={props.inview && props.inview.triggerOnce ? props.inview.triggerOnce : false}
            threshold={props.inview && props.inview.threshold ? props.inview.threshold : 0.1}
            id={`io-${options && options.key ? options.key : props.index}`}
            className={getClassName(options, isAlternateGalleryBlock ? "alt_block_classname" : "block_classname", "container")}
            key={options && options.key ? options.key : `block-${props.index}`}
          >
            <div className={getClassName(options, "inner_classname", "py-5")}>
              {(props.title || props.intro) && (
                <header className={options && options.style ? options.style.header_classname : ""}>
                  {props.title && (<h2 className={getClassName(options, "title_classname", "mb-3")}>{props.title}</h2>)}
                  {props.intro && (<p className={getClassName(options, "intro_classname", "")}>{props.intro}</p>)}
                </header>
              )}

              <div className={getClassName(options, "content_classname", "")}>
                <ul className={getClassName(options, isAlternateGalleryBlock ? "alt_gallery_list_classname" : "gallery_list_classname", "list-unstyled mb-0 row")}>
                  {images.items.map((value, index) =>
                    ImageGalleryItem(
                      s,
                      {
                        index: props.index,
                        is_first: index == 0,
                        is_last: index + 1 >= props.images.items.count(),
                        id: value.image,
                        image: value.image_size,
                        image_alt: value.image_alt,
                        key: value.image + `${index}`,
                        get_default_data_src: isAlternateGalleryBlock ? props.thumbnail_rectangle_src : props.thumbnail_square_src,
                        get_default_data_srcset: isAlternateGalleryBlock ? props.thumbnail_rectangle_srcset : props.thumbnail_square_srcset,
                        additional_info: index + 1 >= images.items.count() && images.remaining_items > 0 ? `+${images.remaining_items}` : undefined,
                        caption: value.caption,
                        source: value.source,
                        copy: props.copy,
                        isOpen: value.is_foreground,
                        imageDataSizes: value.imageDataSizes
                      },
                      index,
                      {...options, isAlternateGalleryBlock: isAlternateGalleryBlock }
                    ).run(ondoneImage(setState, props, index))
                  ).toArray()}
                </ul>
              </div>

              {openImage && ImageGalleryOverlay(
                s,
                {
                  index: props.index,
                  is_first: openImageIndex == 0,
                  is_last: openImageIndex + 1 >= props.images.items.count(),
                  id: openImage.image,
                  image: openImage.image_size,
                  image_alt: openImage.image_alt,
                  key: openImage.image + `${openImageIndex}`,
                  get_default_data_src: props.lightbox_src,
                  get_default_data_srcset: props.lightbox_srcset,
                  additional_info: openImageIndex + 1 >= images.items.count() && images.remaining_items > 0 ? `+${images.remaining_items}` : undefined,
                  caption: openImage.caption,
                  source: openImage.source,
                  copy: props.copy,
                  isOpen: openImage.is_foreground,
                  imageDataSizes: openImage.imageDataSizes
                },
                openImageIndex,
                options
              ).run(ondoneImage(setState, props, openImageIndex))}
            </div>
          </InView>
        )
      }
    )
}
  

export type ImageGalleryItemProps =  {
  index: number
  is_first: boolean
  is_last: boolean
  key?: string
  id: Id
  image: ImageSize
  image_alt: string
  imageDataSizes?: string
  get_default_data_src?: (() => ImageSize)
  get_default_data_srcset?: (() => [ImageSize, string][])
  lightbox_src?: (() => ImageSize),
  lightbox_srcset?: (() => [ImageSize, string][])
  additional_info?: string
  caption?: string
  source?: string
  copy: ImageGalleryCopy
  isOpen: boolean
}


export type ImageGalleryItemStyle = Partial<{
  gallery_item_classname: string
  alt_gallery_item_classname: string
  gallery_button_classname: string
  alt_gallery_button_classname: string
  gallery_image_classname: string
  gallery_toggle_classname: string
  lightbox_classname: string
  lightbox_inner_classname: string
  lightbox_figure_classname: string
  lightbox_visual_classname: string
  lightbox_image_classname: string
  lightbox_figcaption_classname: string
  lightbox_caption_classname: string
  lightbox_source_classname: string
  lightbox_nav_classname: string
  lightbox_nav_inner_classname: string
  lightbox_controls_classname: string
  lightbox_controls_inner_classname: string
  lightbox_next_classname: string
  lightbox_prev_classname: string
  lightbox_close_classname: string
  icon_close_id: string
  icon_previous_id: string
  icon_next_id: string
}>


function ImageGalleryItem<customBlockData, TNever>(
  cmsState: CMSState,
  props: ImageGalleryItemProps,
  index: number,
  options?: { style?: ImageGalleryItemStyle; key?: string, isAlternateGalleryBlock?: boolean },
): Widget<Updater<"next" | "prev" | "close" | "open">> {
  return fromJSX(setState => {
      return (
        <li className={getClassName(options, options.isAlternateGalleryBlock ? "alt_gallery_item_classname" : "gallery_item_classname", "col-sm-6 col-md-4 pb-3")} key={`image-${options && options.key ? options.key : props.index}-${index}`}>
          <button
            onClick={e => { setState(_ => "open"); e.stopPropagation(); document.documentElement.classList.add('lightbox--open'); }}
            className={getClassName(options, options.isAlternateGalleryBlock ? "alt_gallery_button_classname" : "gallery_button_classname", "btn p-0 w-100") + (props.additional_info ? " has-toggle" : '')}
            aria-expanded={props.isOpen}
            aria-controls={`a11y-lightbox-${props.id}`}
          >
            {props.isOpen}
            {cmsState.images.has(props.id) &&
              ModernImageContainer(
                cmsState.images.get(props.id),
                getClassName(options, "gallery_image_classname", "w-100"),
                props.image_alt,
                props.get_default_data_src,
                props.get_default_data_srcset,
                options.isAlternateGalleryBlock ? '(min-width: 1800px) 750px, (min-width: 768px) 50vw, 100vw' : index == 0 ? '(min-width: 1800px) 792px, (min-width: 768px) 55vw, 100vw' : index == 1 ? '(min-width: 1800px) 648px, (min-width: 768px) 45vw, 80vw' : '(min-width: 1800px) 576px, (min-width: 768px) 40vw, 70vw',
                true,
                options.isAlternateGalleryBlock ? '720': '580',
                options.isAlternateGalleryBlock ? '360': '580',
              ).run(_ => { })}
            {props.additional_info ? (
              <div className={getClassName(options, "gallery_toggle_classname", "")}>
                {props.additional_info}
                <span className="sr-text">{props.copy.more}</span>
              </div>
            ) : (
              <span className="sr-text">{props.copy.enlarged}</span>
            )}
          </button>
        </li>
      )
    }
  )
}


function ImageGalleryOverlay<customBlockData, TNever>(
  cmsState: CMSState,
  props: ImageGalleryItemProps,
  index: number,
  options?: { style?: ImageGalleryItemStyle; key?: string },
): Widget<Updater<"next" | "prev" | "close" | "open">> {
  return fromJSX(setState => {
      return (
        <div
          id={`a11y-lightbox-${props.id}`}
          aria-hidden="true"
          className={getClassName(options, "lightbox_classname", "mt-3 p-5 bg-light")}
          onClick={(e) => { setState(_ => "close");  e.stopPropagation(); document.documentElement.classList.remove('lightbox--open'); }}
          key={`overlay-${options && options.key ? options.key : props.index}-${index}`}
        >
          <div className={getClassName(options, "lightbox_inner_classname", "")}>
            <div className={getClassName(options, "lightbox_nav_classname", "")}>
              <div className={getClassName(options, "lightbox_nav_inner_classname", "")}>
                <button
                  onClick={(e) => { setState(_ => "close"); e.stopPropagation(); document.documentElement.classList.remove('lightbox--open'); }}
                  className={getClassName(options, "lightbox_close_classname", "btn btn-outline-dark d-block ml-auto")}
                >
                  <span className="sr-text">{props.copy.close}</span>
                  <svg aria-hidden="true"><use xlinkHref={getClassName(options, "icon_close_id", "#icon--cross")} /></svg>
                </button>
              </div>
            </div>
            
            <figure className={getClassName(options, "lightbox_figure_classname", "mt-3")}>
              <div className={getClassName(options, "lightbox_visual_classname", "")}>
                {cmsState.images.has(props.id) &&
                  ModernImageContainer(
                    cmsState.images.get(props.id),
                    getClassName(options, "lightbox_image_classname", "w-100"),
                    props.image_alt,
                    props.get_default_data_src,
                    props.get_default_data_srcset,
                    props.imageDataSizes,
                    true
                  ).run(_ => { })}
                {(props.caption || props.source) && (
                  <figcaption className={getClassName(options, "lightbox_figcaption_classname", "mt-2 text-center")}>
                    {props.caption ? <p className={getClassName(options, "lightbox_caption_classname", "m-0 small text-secondary")}>{props.caption}</p> : <></>}
                    {props.source ? <p className={getClassName(options, "lightbox_source_classname", "m-0 small text-secondary")}>{props.source}</p> : <></>}
                  </figcaption>
                )}
              </div>
            </figure>

            <div className={getClassName(options, "lightbox_controls_classname", "")}>
              <div className={getClassName(options, "lightbox_controls_inner_classname", "d-flex justify-content-between")}>
                {!props.is_first
                  ? <button
                      onClick={e => { setState(_ => "prev"); e.stopPropagation(); }}
                      className={getClassName(options, "lightbox_prev_classname", "btn btn-dark")}
                    >
                      <span className="sr-text">{props.copy.previous}</span>
                      <svg aria-hidden="true"><use xlinkHref={getClassName(options, "icon_previous_id", "#icon--chevron-left-large")} /></svg>
                    </button>
                  : ""
                }
                {!props.is_last
                  ? <button
                      onClick={e => { setState(_ => "next"); e.stopPropagation(); }}
                      className={getClassName(options, "lightbox_next_classname", "btn btn-dark ml-auto")}
                    >
                      <span className="sr-text">{props.copy.next}</span>
                      <svg aria-hidden="true"><use xlinkHref={getClassName(options, "icon_next_id", "#icon--chevron-right-large")} /></svg>
                    </button>
                  : ""
                }
              </div>
            </div>
          </div>
        </div>
      )
    }
  )
}
  

export class ListWithAtLeast2Items<T> {
  public items: List<T>
  public constructor(images: List<T>) {
    if (images.count() < 2) {
      throw `ListWithAtLeast2Items: expected at least 2 items, but received ${images.count()}`
    }
    this.items = images
  }
  public GetFirst(n: number): { items: List<T>, remaining_items: number } {
    if (this.items.count() <= n) return { items: this.items, remaining_items: 0 }
    let selected_images = this.items.take(n).toList()
    return { items: selected_images, remaining_items: this.items.count() - selected_images.count() }
  }
}
